// @flow
import React, { PureComponent } from "react";
import { Message, Select } from "semantic-ui-react";
import { formValues } from "redux-form";
import { connect } from "react-redux";

import { TARIFF_IS_NOT_IN_LIST } from "../../../../Commons/Modules/Provider/misc/Constants";
import i18n from "../../../../i18n";

import type { ProviderT } from "Commons/Modules/Provider/misc/Types";
import type { TariffResponseT } from "Commons/Modules/tariff/misc/Types";
import { genericGtmEvent } from "Commons/Utilities/GoogleTagManager";
import { mapTariffs } from "Commons/Components/FormComponents/misc/Mappers";

type Props = {
  locationId: ?string,
  postalCode: ?string,
  tariffId: ?string,
  placeholder: ?string,
  loading: ?boolean,
  name: string,
  providerId: ?string,
  consumption: ?number,
  value: ?number,
  tariffs: TariffResponseT[],
  basicTariffs: TariffResponseT[],
  basicTariff: TariffResponseT,
  providers: ProviderT[],
  gtm?: ?Object,
  change: Function,
  meta: {
    error: ?string,
    touched: boolean,
  },
};

const NOT_IN_LIST_OPTION = {
  value: TARIFF_IS_NOT_IN_LIST,
  text: i18n.t("common:notInList"),
};

let DynamicSelectTariffs = class DynamicSelectTariffs extends PureComponent<Props> {
  componentDidUpdate(prevProps: Props) {
    if (this.props.tariffs && this.props.tariffs.length !== 0 && this.props.providers.length === 0) {
      this.props.change("tariffs", []);
    }

    // If a different provider was selected
    // preselect basic tariff again (if available)
    if (this.props.providerId !== prevProps.providerId) {
      if (this.props.basicTariff) {
        this.props.change("tariffId", this.props.basicTariff.id);
        this.props.change("tariffName", this.props.basicTariff.name.text);
        this.props.change("permanentTariffId", this.props.basicTariff.permanentId);
      } else {
        this.props.change("tariffId", null);
        this.props.change("tariffName", null);
        this.props.change("permanentTariffId", null);
      }
    }

    // Calling change from redux form updates the store,
    // which will trigger another render, we need to check
    // if values are different to avoid an infinite loop
    const { basicTariff, value } = this.props;
    if (basicTariff) {
      if (value === "" && basicTariff.id !== prevProps.tariffId) {
        this.props.change("tariffId", basicTariff.id);
        this.props.change("tariffName", basicTariff.name.text);
        this.props.change("permanentTariffId", basicTariff.permanentId);
      }
    }
  }

  render() {
    let {
      meta: { error, touched },
      tariffs,
      basicTariffs,
      basicTariff,
      value,
      loading,
    } = this.props;

    let options = [];

    if (tariffs) {
      options = [...basicTariffs, ...tariffs];
    }

    if (basicTariff) {
      options.unshift({
        ...basicTariff,
        id: NOT_IN_LIST_OPTION.value,
        name: {
          ...basicTariff.name,
          text: NOT_IN_LIST_OPTION.text,
        },
      });
    }

    return (
      <>
        <Select
          lazyLoad
          fluid
          search
          data-cy="tariff-select"
          icon="search"
          loading={loading}
          value={value}
          disabled={options.length === 0}
          options={options.map(mapTariffs)}
          error={!!error && touched}
          noResultsMessage={i18n.t("common:notResultsFound")}
          onChange={(_, { value }) => {
            const payload = this.props.gtm?.payload ? { clickedStep: this.props.gtm.payload } : null;
            this.props.gtm && genericGtmEvent(this.props.gtm.event, payload);
            const chosenOption: ?TariffResponseT = options.find((tariff: TariffResponseT) => tariff.id === value);

            if (chosenOption) {
              // Not in the list option is selected
              if (value === NOT_IN_LIST_OPTION.value) {
                this.props.change("tariffId", basicTariff.id);
                this.props.change("tariffName", basicTariff.name.text);
              } else {
                this.props.change("tariffId", value);
                this.props.change("tariffName", chosenOption.name.text);
              }

              this.props.change("permanentTariffId", chosenOption.permanentId);
            }
          }}
        />
        {!!error && touched && <Message negative content={error} />}
      </>
    );
  }
};

const mapStateToProps = ({ providers }, props) => {
  let basicTariffs = [];
  const provider = (providers.providers[props.categoryType] || []).find(
    (provider) => provider.id === props.categoryProviderId
  );

  if (provider) {
    basicTariffs = provider.tariffs.product
      .filter((tariff) => tariff.isBasicTariff)
      .map((tariff) => ({
        ...tariff,
        name: {
          ...tariff.name,
          text: `${tariff.name.text} (Standardtarif)`,
        },
      }));
  }

  return {
    basicTariffs,
    basicTariff: basicTariffs[0],
    tariffs: (props.tariffs || []).filter((tariff) => !tariff.isBasicTariff),
    providers: providers.providers,
  };
};

DynamicSelectTariffs = connect(mapStateToProps)(DynamicSelectTariffs);

DynamicSelectTariffs = formValues({
  tariffId: "tariffId",
  providerId: "companyId",
  consumption: "consumption",
  tariffs: "tariffs",
  categoryProviderId: "categoryProviderId",
})(DynamicSelectTariffs);

export default DynamicSelectTariffs;
